import { Button } from "primereact/button";
import styled, { createGlobalStyle } from "styled-components";
import { MODULES } from "training/curriculum/modules";
import { advanceKind, POPOVER_CLASS } from "training/engine/steps";
import { ITourStep, TourModule } from "training/types";

/**
 * CLE-3015 — the React content portalled into the driver.js popover: where the trainee is ("Module 2 ·
 * step 7 of 16" and the module's title; on a module's first step the title as a heading and the module's
 * one-sentence purpose instead), the on-screen label the step is about, the curriculum text, an optional picture, and
 * the buttons. driver.js's own title and buttons are not used (every step passes `showButtons: []`), so
 * this component is the whole popover body.
 *
 * - Next appears on every step that moves on with Next (look, fill, card, picture); a `click` step moves
 *   on when the trainee clicks the highlighted element, an `event` step when the page reports it.
 * - Back re-shows the previous step on this page (e.g. to fix a field Create and Send refused).
 * - Exit saves where the trainee is and closes the tour; the banner's Resume brings them back.
 */
export const TourPopoverGlobalStyle = createGlobalStyle`
  .driver-popover.${POPOVER_CLASS} {
    max-width: 380px;
    min-width: 280px;
    padding: 16px;
    border: 1px solid var(--brand-gray-200);
    border-radius: 12px;
    font-family: var(--font-family);
    color: var(--brand-black);
  }

  .driver-popover.${POPOVER_CLASS} .driver-popover-title,
  .driver-popover.${POPOVER_CLASS} .driver-popover-footer,
  .driver-popover.${POPOVER_CLASS} .driver-popover-close-btn {
    display: none !important;
  }

  .driver-popover.${POPOVER_CLASS} .driver-popover-description {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.45;
  }
`;

const Header = styled.div`
  margin-bottom: 8px;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--brand-purple-700);
  font-size: 0.75rem;
  font-weight: var(--font-weight-semibold);
`;

const ModuleTitle = styled.div`
  margin-top: 2px;
  color: var(--brand-gray-600);
  font-size: 0.75rem;
`;

/** A module's first step: what the module is about, above the step itself. */
const Intro = styled.div`
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--brand-gray-200);
`;

const IntroTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: var(--font-weight-semibold);
`;

const IntroPurpose = styled.p`
  margin: 0;
  color: var(--brand-gray-700);
`;

const StepLabel = styled.div`
  margin-bottom: 6px;
  font-size: 0.95rem;
  font-weight: var(--font-weight-semibold);
`;

const Paragraph = styled.p`
  margin: 0 0 8px;
`;

/** Consecutive "Label: explanation" lines (the statuses on 1.5), one per line with little spacing. */
const TermList = styled.ul`
  margin: 0 0 8px;
  padding: 0;
  list-style: none;

  li + li {
    margin-top: 2px;
  }
`;

/** "Action Needed: a draft, ..." -> label and explanation. A short label, no sentence end inside it. */
const TERM_LINE = /^([^:.]{1,30}): (.+)$/;

type TextBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "terms"; lines: Array<{ term: string; rest: string }> };

/** Paragraphs from the step text, with runs of "Label: explanation" lines gathered into one list. */
export function textBlocks(text: string): TextBlock[] {
  const blocks: TextBlock[] = [];
  text
    .split("\n")
    .filter(Boolean)
    .forEach((line) => {
      const match = TERM_LINE.exec(line);
      if (!match) {
        blocks.push({ kind: "paragraph", text: line });
        return;
      }
      const last = blocks[blocks.length - 1];
      const entry = { term: match[1], rest: match[2] };
      if (last?.kind === "terms") last.lines.push(entry);
      else blocks.push({ kind: "terms", lines: [entry] });
    });
  return blocks;
}

const Hint = styled.p`
  margin: 4px 0 0;
  color: var(--brand-gray-600);
  font-size: 0.8rem;
  font-style: italic;
`;

const Picture = styled.img`
  display: block;
  width: 100%;
  margin: 8px 0;
  border: 1px solid var(--brand-gray-200);
  border-radius: 8px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
`;

export interface ITourPosition {
  module: TourModule;
  index: number;
  total: number;
}

interface ITourPopoverProps {
  step: ITourStep;
  position: ITourPosition | null;
  canGoBack: boolean;
  onNext: () => void;
  onBack: () => void;
  onExit: () => void;
}

export function TourPopover({
  step,
  position,
  canGoBack,
  onNext,
  onBack,
  onExit,
}: ITourPopoverProps) {
  const kind = advanceKind(step);
  const moduleInfo = position ? MODULES[position.module] : undefined;
  const firstOfModule = !!moduleInfo && position?.index === 1;

  return (
    <div data-testid="tour-popover" role="dialog" aria-label="Clear Certified">
      <Header>
        <Eyebrow>
          <i className="pi pi-verified" aria-hidden />
          <span>
            {position
              ? `Module ${position.module} · step ${position.index} of ${position.total}`
              : "Clear Certified"}
          </span>
        </Eyebrow>
        {moduleInfo && !firstOfModule && (
          <ModuleTitle data-testid="tour-module-title">
            {moduleInfo.title}
          </ModuleTitle>
        )}
      </Header>

      {firstOfModule && moduleInfo && (
        <Intro data-testid="tour-module-intro">
          <IntroTitle>{moduleInfo.title}</IntroTitle>
          <IntroPurpose>{moduleInfo.purpose}</IntroPurpose>
        </Intro>
      )}

      {step.label && <StepLabel>{step.label}</StepLabel>}

      {textBlocks(step.text).map((block) =>
        block.kind === "paragraph" ? (
          <Paragraph key={block.text}>{block.text}</Paragraph>
        ) : (
          <TermList key={block.lines[0].term}>
            {block.lines.map(({ term, rest }) => (
              <li key={term}>
                <strong>{term}:</strong> {rest}
              </li>
            ))}
          </TermList>
        )
      )}

      {step.picture && <Picture src={step.picture} alt="" />}

      {kind === "click" && (
        <Hint>Click the highlighted button to continue.</Hint>
      )}
      {kind === "event" && (
        <Hint>The tour continues once your estimate is created.</Hint>
      )}

      <Footer>
        <Button
          type="button"
          label="Exit"
          className="p-button-text p-button-plain p-button-sm"
          onClick={onExit}
        />
        <div className="flex gap-2">
          {canGoBack && (
            <Button
              type="button"
              label="Back"
              className="p-button-outlined p-button-sm"
              onClick={onBack}
            />
          )}
          {kind === "next" && (
            <Button
              type="button"
              label={step.continueLabel ?? "Next"}
              className="p-button-sm"
              onClick={onNext}
            />
          )}
        </div>
      </Footer>
    </div>
  );
}
